/**
 * Simple yet effective Markdown renderer for AI responses.
 * Handles code blocks, inline code, bold, italic, lists, and headers.
 */
import React from 'react';

interface MarkdownProps {
  content: string;
  onInsertCode?: (code: string, language: string) => void;
  onCreateFile?: (code: string, language: string) => void;
  onApplyCode?: (code: string, language: string) => void;
}

interface ParsedBlock {
  type: 'code' | 'text';
  content: string;
  language?: string;
}

function parseBlocks(content: string): ParsedBlock[] {
  const blocks: ParsedBlock[] = [];
  const codeBlockRegex = /```(\w*)\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = codeBlockRegex.exec(content)) !== null) {
    // Text before code block
    if (match.index > lastIndex) {
      blocks.push({
        type: 'text',
        content: content.slice(lastIndex, match.index),
      });
    }
    blocks.push({
      type: 'code',
      content: match[2].trimEnd(),
      language: match[1] || 'solidity',
    });
    lastIndex = match.index + match[0].length;
  }

  // Remaining text
  if (lastIndex < content.length) {
    blocks.push({ type: 'text', content: content.slice(lastIndex) });
  }

  return blocks;
}

function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Regex for inline code, bold, italic
  const inlineRegex = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g;
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = inlineRegex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(text.slice(lastIdx, match.index));
    }
    const raw = match[0];
    if (raw.startsWith('`')) {
      parts.push(
        <code
          key={key++}
          className="px-1 py-0.5 bg-[#333] rounded text-[11px] text-blue-300 font-mono"
        >
          {raw.slice(1, -1)}
        </code>
      );
    } else if (raw.startsWith('**')) {
      parts.push(
        <strong key={key++} className="font-semibold text-white">
          {raw.slice(2, -2)}
        </strong>
      );
    } else if (raw.startsWith('*') || raw.startsWith('_')) {
      parts.push(
        <em key={key++} className="italic text-gray-300">
          {raw.slice(1, -1)}
        </em>
      );
    }
    lastIdx = match.index + match[0].length;
  }

  if (lastIdx < text.length) {
    parts.push(text.slice(lastIdx));
  }

  return parts;
}

function renderTextBlock(text: string): React.ReactNode {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  const flushList = () => {
    if (listItems.length === 0) return;
    const Tag = listType === 'ol' ? 'ol' : 'ul';
    elements.push(
      <Tag
        key={elements.length}
        className={`${listType === 'ol' ? 'list-decimal' : 'list-disc'} list-inside text-xs text-[#d4d4d4] space-y-0.5 my-1`}
      >
        {listItems.map((item, i) => (
          <li key={i}>{renderInlineMarkdown(item)}</li>
        ))}
      </Tag>
    );
    listItems = [];
    listType = null;
  };

  for (const line of lines) {
    const trimmed = line.trim();

    // Headers
    if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(
        <h4 key={elements.length} className="text-xs font-semibold text-white mt-2 mb-0.5">
          {renderInlineMarkdown(trimmed.slice(4))}
        </h4>
      );
      continue;
    }
    if (trimmed.startsWith('## ')) {
      flushList();
      elements.push(
        <h3 key={elements.length} className="text-sm font-semibold text-white mt-2 mb-0.5">
          {renderInlineMarkdown(trimmed.slice(3))}
        </h3>
      );
      continue;
    }
    if (trimmed.startsWith('# ')) {
      flushList();
      elements.push(
        <h2 key={elements.length} className="text-sm font-bold text-white mt-2 mb-1">
          {renderInlineMarkdown(trimmed.slice(2))}
        </h2>
      );
      continue;
    }

    // Unordered list
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(trimmed.slice(2));
      continue;
    }

    // Ordered list
    const olMatch = trimmed.match(/^\d+\.\s/);
    if (olMatch) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(trimmed.slice(olMatch[0].length));
      continue;
    }

    // Horizontal rule
    if (trimmed === '---' || trimmed === '***') {
      flushList();
      elements.push(<hr key={elements.length} className="border-[#444] my-2" />);
      continue;
    }

    // Empty line
    if (trimmed === '') {
      flushList();
      continue;
    }

    // Regular paragraph
    flushList();
    elements.push(
      <p key={elements.length} className="text-xs text-[#d4d4d4] leading-relaxed my-0.5">
        {renderInlineMarkdown(line)}
      </p>
    );
  }

  flushList();
  return <>{elements}</>;
}

export default function MarkdownRenderer({ content, onInsertCode, onCreateFile, onApplyCode }: MarkdownProps) {
  const blocks = parseBlocks(content);

  return (
    <div className="space-y-1">
      {blocks.map((block, i) => {
        if (block.type === 'code') {
          return (
            <div key={i} className="group relative">
              <div className="flex items-center justify-between px-2 py-1 bg-[#2d2d2d] rounded-t border border-[#444] border-b-0 text-[9px] text-gray-500">
                <span className="uppercase">{block.language}</span>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => navigator.clipboard.writeText(block.content)}
                    className="px-1.5 py-0.5 bg-[#444] hover:bg-[#555] text-gray-300 rounded text-[9px] transition-colors"
                  >
                    Copy
                  </button>
                  {onInsertCode && (
                    <button
                      onClick={() => onInsertCode(block.content, block.language ?? 'solidity')}
                      className="px-1.5 py-0.5 bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 rounded text-[9px] transition-colors"
                    >
                      Insert
                    </button>
                  )}
                  {onApplyCode && (
                    <button
                      onClick={() => onApplyCode(block.content, block.language ?? 'solidity')}
                      className="px-1.5 py-0.5 bg-yellow-600/30 hover:bg-yellow-600/50 text-yellow-300 rounded text-[9px] transition-colors"
                      title="Replace current file content"
                    >
                      Apply
                    </button>
                  )}
                  {onCreateFile && (
                    <button
                      onClick={() => onCreateFile(block.content, block.language ?? 'solidity')}
                      className="px-1.5 py-0.5 bg-green-600/30 hover:bg-green-600/50 text-green-300 rounded text-[9px] transition-colors"
                      title="Create a new file with this code"
                    >
                      New File
                    </button>
                  )}
                </div>
              </div>
              <pre className="bg-[#1a1a1a] border border-[#444] border-t-0 rounded-b p-2 overflow-x-auto">
                <code className="text-[11px] leading-relaxed text-[#d4d4d4] font-mono whitespace-pre">
                  {block.content}
                </code>
              </pre>
            </div>
          );
        }
        return <React.Fragment key={i}>{renderTextBlock(block.content)}</React.Fragment>;
      })}
    </div>
  );
}
